import { useMemo, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { sharedStyles as s } from "./identityVariants/shared.tsx";

const REPLENISH_STATUSES = [
  { id: "1", title: "Ordered" },
  { id: "2", title: "Partially stocked" },
  { id: "3", title: "Partially finished" },
  { id: "4", title: "Finished" },
] as const;

function parseSelected(value: string): string[] {
  if (!value) return [];
  return value.split(",").map((x) => x.trim()).filter(Boolean);
}

/**
 * Replenish-status filter — segmented toggle pills, matching the Bulk tab's
 * group-field picker visual language. Emits a single InputParam with the
 * selected status IDs joined by comma (server expects csv). On first mount
 * with no incoming value, defaults to "all selected" (WebAdmin2 behaviour).
 */
export function ReplenishFilter({ info, value, onChange, onSearch }: AdvancedFilterProps) {
  const allIds = useMemo(() => REPLENISH_STATUSES.map((x) => x.id), []);

  const [selected, setSelected] = useState<string[]>(() => {
    if (value.length > 0) {
      return value[0]?.oValue != null ? parseSelected(String(value[0].oValue)) : [];
    }
    return allIds;
  });

  const apply = (next: string[]) => {
    setSelected(next);
    if (next.length === 0) onChange([]);
    else onChange([{ sColumn: info.ColumnName, oValue: next.join(","), Type: ParamType.In }]);
  };

  const toggle = (id: string) => {
    const has = selected.includes(id);
    apply(has ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const isAll = selected.length === REPLENISH_STATUSES.length;
  const isNone = selected.length === 0;

  return (
    <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", minWidth: 0, flexWrap: "nowrap" }}>
      <div className={s.seg} style={{ flexWrap: "nowrap" }}>
        {REPLENISH_STATUSES.map((status) => (
          <button
            key={status.id}
            type="button"
            className={selected.includes(status.id) ? s.segBtnActive : s.segBtn}
            onClick={() => toggle(status.id)}
          >
            {status.title}
          </button>
        ))}
      </div>
      <button type="button" className={s.btnGhost} onClick={() => apply(allIds)} disabled={isAll}>
        All
      </button>
      <button type="button" className={s.btnGhost} onClick={() => apply([])} disabled={isNone}>
        Clear
      </button>
      <div style={{ flex: 1 }} />
      <button type="button" className={s.btn} onClick={onSearch}>
        Apply
      </button>
    </div>
  );
}
